import React, { useState, useEffect } from 'react';
import {
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  CircleStackIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import Button from '../../components/UI/Button';
import StatsCard from '../../components/UI/StatsCard';
import DataTable, { TableColumn, TableAction } from '../../components/UI/DataTable';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import { backupService, BackupRecord } from '../../services/backupService';

const BackupPage: React.FC = () => {
  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [deleteBackup, setDeleteBackup] = useState<BackupRecord | null>(null);

  // Load backups from API
  useEffect(() => {
    const loadBackups = async () => {
      setLoading(true);
      try {
        const res = await backupService.list();
        setBackups(res.backups || []);
      } catch (error) {
        console.error('Failed to load backups:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBackups();
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: BackupRecord['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'IN_PROGRESS':
        return <ClockIcon className="w-4 h-4 text-yellow-500" />;
      case 'FAILED':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      case 'PENDING':
        return <ClockIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: BackupRecord['status']) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case 'COMPLETED':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'IN_PROGRESS':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'FAILED':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'PENDING':
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getTypeBadge = (type: BackupRecord['type']) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (type) {
      case 'FULL':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'PARTIAL':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'INCREMENTAL':
        return `${baseClasses} bg-indigo-100 text-indigo-800`;
    }
  };

  const statsData = [
    {
      id: 'total-backups',
      title: 'Total Backups',
      value: backups.length,
      icon: <CircleStackIcon className="w-6 h-6" />,
      color: 'blue' as const,
    },
    {
      id: 'successful-backups',
      title: 'Successful',
      value: backups.filter(b => b.status === 'COMPLETED').length,
      icon: <CheckCircleIcon className="w-6 h-6" />,
      color: 'green' as const,
    },
    {
      id: 'total-size',
      title: 'Total Size',
      value: formatFileSize(backups.reduce((sum, b) => sum + b.size, 0)),
      icon: <CloudArrowDownIcon className="w-6 h-6" />,
      color: 'purple' as const,
    },
    {
      id: 'last-backup',
      title: 'Last Backup',
      value: backups.length > 0 ? new Date(backups[0].createdAt).toLocaleDateString() : 'Never',
      icon: <ClockIcon className="w-6 h-6" />,
      color: 'yellow' as const,
    },
  ];

  const columns: TableColumn<BackupRecord>[] = [
    {
      key: 'name',
      header: 'Backup Name',
      sortable: true,
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (value) => (
        <span className={getTypeBadge(value)}>{value}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-2">
          {getStatusIcon(value)}
          <span className={getStatusBadge(value)}>{value}</span>
        </div>
      ),
    },
    {
      key: 'size',
      header: 'Size',
      sortable: true,
      render: (value) => formatFileSize(value),
    },
    {
      key: 'recordCount',
      header: 'Records',
      sortable: true,
      render: (value) => value.toLocaleString(),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (value) => new Date(value).toLocaleString(),
    },
    {
      key: 'completedAt',
      header: 'Completed',
      render: (value) => value ? new Date(value).toLocaleString() : '-',
    },
  ];

  const actions: TableAction<BackupRecord>[] = [
    {
      key: 'download',
      label: 'Download',
      onClick: async (backup) => {
        try {
          const blob = await backupService.download(backup.id);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${backup.id}.json`;
          a.click();
          window.URL.revokeObjectURL(url);
        } catch (e) {
          console.error('Download failed', e);
        }
      },
      show: (backup) => backup.status === 'COMPLETED',
    },
    {
      key: 'restore',
      label: 'Restore',
      onClick: (backup) => {
        handleRestore(backup);
      },
      show: (backup) => backup.status === 'COMPLETED',
    },
    {
      key: 'delete',
      label: 'Delete',
      variant: 'danger',
      onClick: (backup) => {
        setDeleteBackup(backup);
      },
    },
  ];

  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      await backupService.create({ type: 'FULL' });
      const res = await backupService.list();
      setBackups(res.backups || []);
    } catch (error) {
      console.error('Failed to create backup:', error);
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleRestore = async (backup: BackupRecord) => {
    if (window.confirm(`Are you sure you want to restore from "${backup.name}"? This will overwrite current data.`)) {
      setIsRestoring(true);
      try {
        await backupService.restore(backup.id);
      } catch (error) {
        console.error('Failed to restore backup:', error);
      } finally {
        setIsRestoring(false);
      }
    }
  };

  const handleDeleteBackup = async () => {
    if (deleteBackup) {
      try {
        await backupService.remove(deleteBackup.id);
        const res = await backupService.list();
        setBackups(res.backups || []);
        setDeleteBackup(null);
      } catch (error) {
        console.error('Failed to delete backup:', error);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Backup & Restore</h1>
          <p className="text-gray-600">Manage system backups and data restoration</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="secondary"
            onClick={() => console.log('Import backup')}
            className="flex items-center"
            disabled={isRestoring}
          >
            <CloudArrowUpIcon className="w-4 h-4 mr-2" />
            Import Backup
          </Button>
          <Button
            onClick={handleCreateBackup}
            loading={isCreatingBackup}
            className="flex items-center"
          >
            <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
            Create Backup
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCard stats={statsData} />

      {/* Backup Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-soft p-6">
          <div className="flex items-center mb-4">
            <CircleStackIcon className="w-8 h-8 text-blue-500 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Full Backup</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Complete backup of all system data including clients, ingredients, diets, menus, recipes, and payments.
          </p>
          <Button
            variant="secondary"
            onClick={handleCreateBackup}
            loading={isCreatingBackup}
            className="w-full"
          >
            Create Full Backup
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-soft p-6">
          <div className="flex items-center mb-4">
            <CloudArrowDownIcon className="w-8 h-8 text-purple-500 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Partial Backup</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Selective backup of specific data tables. Choose which data to include in the backup.
          </p>
          <Button
            variant="secondary"
            onClick={() => console.log('Create partial backup')}
            className="w-full"
          >
            Create Partial Backup
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-soft p-6">
          <div className="flex items-center mb-4">
            <ClockIcon className="w-8 h-8 text-green-500 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Scheduled Backup</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Set up automatic backups to run daily, weekly, or monthly to ensure data safety.
          </p>
          <Button
            variant="secondary"
            onClick={() => console.log('Configure scheduled backup')}
            className="w-full"
          >
            Configure Schedule
          </Button>
        </div>
      </div>

      {/* Backup History */}
      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Backup History</h3>
          {isRestoring && (
            <div className="flex items-center text-yellow-600">
              <ClockIcon className="w-4 h-4 mr-2" />
              <span className="text-sm">Restoring backup...</span>
            </div>
          )}
        </div>

        <DataTable
          columns={columns}
          data={backups}
          loading={loading}
          actions={actions}
          emptyMessage="No backups found"
        />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteBackup && (
        <DeleteConfirmModal
          onCancel={() => setDeleteBackup(null)}
          onConfirm={handleDeleteBackup}
          title="Delete Backup"
          message={`Are you sure you want to delete the backup "${deleteBackup.name}"? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default BackupPage;
